import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Icons, TRANSFER_TYPES, VEHICLE_TYPES, PRODUCT_STATUSES, MOCK_SUPPLIERS } from "./bookingConstants";
import { useGetAirports } from "../../api/geo/useAirports";
import { useGetHotels } from "../../api/products/useHotelsApi";
import s from "./TransferForm.module.css";

/* ─── Local Constants ────────────────────────────────────────────────────── */

const LOCATION_TYPES = [
  { key: "Airport",  label: "Airport",  icon: "✈" },
  { key: "Hotel",    label: "Hotel",    icon: "🏨" },
  { key: "Address",  label: "Address",  icon: "📍" },
  { key: "Port",     label: "Port",     icon: "⚓" },
  { key: "Station",  label: "Station",  icon: "🚉" },
  { key: "Other",    label: "Other",    icon: "📌" },
];

const EXTENDED_VEHICLE_TYPES = [
  { key: "Car",              label: "Car" },
  { key: "Minibus",          label: "Minibus" },
  { key: "Bus",              label: "Bus" },
  { key: "Van",              label: "Van" },
  { key: "SUV",              label: "SUV" },
  { key: "Shuttle",          label: "Shuttle" },
  { key: "ExecutiveVehicle", label: "Executive Vehicle" },
  { key: "Other",            label: "Other" },
];

const TRANSFER_MOMENTS = [
  { key: "Arrival",   label: "Arrival Transfer",   desc: "Airport → Hotel / Accommodation",     icon: "arrival" },
  { key: "Departure", label: "Departure Transfer",  desc: "Hotel / Accommodation → Airport",     icon: "departure" },
  { key: "RoundTrip", label: "Round Trip",          desc: "Arrival + Departure in one transfer", icon: "roundtrip" },
  { key: "Custom",    label: "Custom Transfer",     desc: "Fully manual route — no prefill",     icon: "custom" },
];

const PICKUP_TIME_TYPES = [
  { key: "exact", label: "Exact Time" },
  { key: "tba",   label: "To Be Advised (TBA)" },
  { key: "voucher", label: "Follow Voucher Instructions" },
];

/* ─── Searchable Select ─────────────────────────────────────── */

const SearchSelect = ({ value, options, onChange, onSearch, placeholder = "Search...", renderOption, renderSelected, onBlur, initialOption }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pickedOption, setPickedOption] = useState(null);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (open && onBlur) onBlur();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onBlur]);

  useEffect(() => {
    if (!onSearch) return;
    const timer = setTimeout(() => onSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, onSearch]);

  useEffect(() => {
    if (pickedOption && pickedOption.value !== value) setPickedOption(null);
  }, [value, pickedOption]);

  const filtered = useMemo(() => {
    if (onSearch) return options;
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o._search || "").toLowerCase().includes(q));
  }, [options, query, onSearch]);

  const handleSelect = (opt) => {
    setPickedOption(opt);
    onChange(opt.value);
    setOpen(false);
    setQuery("");
    if (onBlur) onBlur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setPickedOption(null);
    onChange("");
    setQuery("");
    if (onBlur) onBlur();
  };

  const fromOptions = value ? options.find((o) => o.value === value) : null;
  const selected = fromOptions
    || (pickedOption && pickedOption.value === value ? pickedOption : null)
    || (initialOption && initialOption.value === value ? initialOption : null);

  if (selected && !open) {
    return (
      <div className={s.searchSelect} ref={ref}>
        <div className={s.searchSelectSelected} onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}>
          {renderSelected ? renderSelected(selected) : (
            <>
              <span className={s.searchSelectSelectedCode}>{selected.code}</span>
              <span className={s.searchSelectSelectedName}>{selected.label}</span>
            </>
          )}
          <button type="button" className={s.searchSelectClear} onClick={handleClear}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.searchSelect} ref={ref}>
      <input
        ref={inputRef}
        className={s.searchSelectInput}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div className={s.searchSelectDropdown}>
          {filtered.length === 0 ? (
            <div className={s.searchSelectEmpty}>No results found</div>
          ) : (
            filtered.slice(0, 50).map((opt) => (
              <div
                key={opt.value}
                className={`${s.searchSelectOption} ${opt.value === value ? s.searchSelectOptionActive : ""}`}
                onClick={() => handleSelect(opt)}
              >
                {renderOption ? renderOption(opt) : (
                  <>
                    <span className={s.searchSelectCode}>{opt.code}</span>
                    <span className={s.searchSelectName}>{opt.label}</span>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Inline SVG Icons ───────────────────────────────────────────────────── */

const ArrivalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 22h20"/><path d="M3.77 10.77 2 9l2-4.5 1.1.55c.55.28.9.84.9 1.45s.35 1.17.9 1.45L8 8l4.8-2.65c.57-.31 1.27-.08 1.56.5l.86 1.72-8.94 4.93c-.56.31-1.26.1-1.6-.42L3.76 10.78Z"/>
    <path d="m22 2-4.6 4.6M15.8 4.4l2 2"/>
  </svg>
);

const DepartureIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 22h20"/><path d="M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12l5 2.18a2 2 0 0 1 1.1 1.63l.11 1.72-9.29 1.36a1 1 0 0 1-1.11-.64l-.45-1.24Z"/>
    <path d="M22 2l-4.6 4.6M15.8 4.4l2 2"/>
  </svg>
);

const CustomIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>
  </svg>
);

const RoundTripIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <path d="M7 21l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const UnlinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71"/><path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71"/>
    <line x1="8" y1="2" x2="8" y2="5"/><line x1="2" y1="8" x2="5" y2="8"/><line x1="16" y1="19" x2="16" y2="22"/><line x1="19" y1="16" x2="22" y2="16"/>
  </svg>
);

const RouteArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const FlightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
  </svg>
);

/* ─── Stepper ───────────────────────────────────────────────────────────── */

const Stepper = ({ value, onChange, min = 0 }) => (
  <div className={s.stepperWrap}>
    <button type="button" className={s.stepperBtn} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
    <input className={`${s.input} ${s.stepperVal}`} type="number" min={min} value={value} onChange={(e) => onChange(parseInt(e.target.value) || min)} />
    <button type="button" className={s.stepperBtn} onClick={() => onChange(value + 1)}>+</button>
  </div>
);

/* ─── Location Type Selector (visual cards) ──────────────────────────────── */

const LocationTypeSelector = ({ value, onChange, label, required }) => (
  <div className={s.field}>
    <label className={s.fieldLabel}>{label} {required && <span className={s.req}>*</span>}</label>
    <div className={s.locTypeGrid}>
      {LOCATION_TYPES.map((lt) => (
        <button
          key={lt.key}
          type="button"
          className={`${s.locTypeCard} ${value === lt.key ? s.locTypeCardActive : ""}`}
          onClick={() => onChange(lt.key)}
        >
          <span className={s.locTypeIcon}>{lt.icon}</span>
          <span className={s.locTypeLabel}>{lt.label}</span>
        </button>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   RoundTripLegPanel — Per-leg sections for Round Trip mode
   ═══════════════════════════════════════════════════════════════════════════ */

const RoundTripLegPanel = ({
  leg, setField, legMoment, label, color,
  flightOptions, bookingHotelName,
  airportOptions, hotelOptions,
  handleAirportSearch, handleHotelSearch,
  otherLeg,
}) => {
  const [flightConnectionMode, setFlightConnectionMode] = useState(leg._linkedFlightId ? "linked" : "manual");

  const selectedFlight = flightOptions.find((fo) => fo.id === leg._linkedFlightId);
  const allFlightLegs = selectedFlight?.legs || [];

  const otherLinkedLegId = otherLeg?._linkedLegId || "";
  const otherLinkedLegData = otherLinkedLegId ? allFlightLegs.find((l) => l.id === otherLinkedLegId) : null;
  const otherLinkedDirection = otherLinkedLegData?.direction || "";

  const availableLegs = useMemo(() => {
    if (!otherLinkedDirection) return allFlightLegs;
    if (legMoment === "Departure" && otherLinkedDirection === "Outbound") {
      return allFlightLegs.filter((l) => l.direction === "Inbound");
    }
    if (legMoment === "Arrival" && otherLinkedDirection === "Inbound") {
      return allFlightLegs.filter((l) => l.direction === "Outbound");
    }
    return allFlightLegs;
  }, [allFlightLegs, otherLinkedDirection, legMoment]);

  const selectedLegData = availableLegs.find((l) => l.id === leg._linkedLegId);

  const handleFlightSelect = (flightId) => {
    setField("_linkedFlightId", flightId);
    setField("_linkedLegId", "");
    const flight = flightOptions.find((fo) => fo.id === flightId);
    if (flight && flight.legs.length > 0) {
      const otherLegData = otherLinkedLegId ? flight.legs.find((l) => l.id === otherLinkedLegId) : null;
      const otherDir = otherLegData?.direction || "";
      let candidateLegs = flight.legs;
      if (legMoment === "Departure" && otherDir === "Outbound") {
        candidateLegs = flight.legs.filter((l) => l.direction === "Inbound");
      } else if (legMoment === "Arrival" && otherDir === "Inbound") {
        candidateLegs = flight.legs.filter((l) => l.direction === "Outbound");
      }
      let autoLeg;
      if (legMoment === "Arrival") {
        const outbound = candidateLegs.filter((l) => l.direction === "Outbound");
        autoLeg = outbound.length > 0 ? outbound[outbound.length - 1] : candidateLegs[candidateLegs.length - 1];
      } else {
        const inbound = candidateLegs.filter((l) => l.direction === "Inbound");
        autoLeg = inbound.length > 0 ? inbound[0] : candidateLegs[0];
      }
      if (autoLeg) handleLegSelect(autoLeg.id, flight.legs);
    }
  };

  const handleLegSelect = (legId, legsOverride) => {
    setField("_linkedLegId", legId);
    const legs = legsOverride || availableLegs;
    const selLeg = legs.find((l) => l.id === legId);
    if (!selLeg) return;

    setField("flightAirlineCode", selLeg.airlineCode || "");
    setField("flightFlightNumber", selLeg.flightNumber || "");
    setField("flightDate", legMoment === "Arrival" ? (selLeg.arrivalDate || selLeg.date || "") : (selLeg.date || ""));
    setField("flightTime", legMoment === "Arrival" ? (selLeg.arrivalTime || selLeg.time || "") : (selLeg.time || ""));

    if (legMoment === "Arrival") {
      setField("pickupLocation", selLeg.arrival || "");
      setField("pickupLocationLabel", selLeg.arrivalName || selLeg.arrival || "");
      setField("pickupLocationType", "Airport");
      if (bookingHotelName && !leg.dropoffLocation) {
        setField("dropoffLocation", bookingHotelName);
        setField("dropoffLocationLabel", bookingHotelName);
        setField("dropoffLocationType", "Hotel");
      }
      setField("pickupDate", selLeg.arrivalDate || selLeg.date || "");
      setField("pickupTime", selLeg.arrivalTime || "");
    } else if (legMoment === "Departure") {
      setField("dropoffLocation", selLeg.departure || "");
      setField("dropoffLocationLabel", selLeg.departureName || selLeg.departure || "");
      setField("dropoffLocationType", "Airport");
      if (otherLeg && otherLeg.dropoffLocation && !leg.pickupLocation) {
        setField("pickupLocation", otherLeg.dropoffLocation);
        setField("pickupLocationLabel", otherLeg.dropoffLocationLabel || otherLeg.dropoffLocation);
        setField("pickupLocationType", otherLeg.dropoffLocationType || "Hotel");
      } else if (bookingHotelName && !leg.pickupLocation) {
        setField("pickupLocation", bookingHotelName);
        setField("pickupLocationLabel", bookingHotelName);
        setField("pickupLocationType", "Hotel");
      }
      setField("pickupDate", selLeg.date || "");
    }
  };

  const handleConnectionToggle = (mode) => {
    setFlightConnectionMode(mode);
    if (mode === "manual") {
      setField("_linkedFlightId", "");
      setField("_linkedLegId", "");
    }
  };

  useEffect(() => {
    if (flightConnectionMode === "linked" && !leg._linkedFlightId && flightOptions.length === 1) {
      handleFlightSelect(flightOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightOptions.length, flightConnectionMode]);

  useEffect(() => {
    if (legMoment === "Arrival" && leg.dropoffLocationType === "Hotel" && !leg.dropoffLocation && bookingHotelName) {
      setField("dropoffLocation", bookingHotelName);
      setField("dropoffLocationLabel", bookingHotelName);
    }
    if (legMoment === "Departure" && !leg.pickupLocation) {
      if (otherLeg && otherLeg.dropoffLocation) {
        setField("pickupLocation", otherLeg.dropoffLocation);
        setField("pickupLocationLabel", otherLeg.dropoffLocationLabel || otherLeg.dropoffLocation);
        setField("pickupLocationType", otherLeg.dropoffLocationType || "Hotel");
      } else if (bookingHotelName && leg.pickupLocationType === "Hotel") {
        setField("pickupLocation", bookingHotelName);
        setField("pickupLocationLabel", bookingHotelName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingHotelName, otherLeg?.dropoffLocation]);

  const airportMismatch = useMemo(() => {
    if (!selectedLegData || flightConnectionMode !== "linked") return null;
    if (legMoment === "Arrival" && leg.pickupLocationType === "Airport") {
      const expected = selectedLegData.arrival || "";
      if (leg.pickupLocation && leg.pickupLocation !== expected) {
        return `Pickup airport (${leg.pickupLocation}) doesn't match the flight's arrival airport (${expected || "unknown"}).`;
      }
    } else if (legMoment === "Departure" && leg.dropoffLocationType === "Airport") {
      const expected = selectedLegData.departure || "";
      if (leg.dropoffLocation && leg.dropoffLocation !== expected) {
        return `Drop-off airport (${leg.dropoffLocation}) doesn't match the flight's departure airport (${expected || "unknown"}).`;
      }
    }
    return null;
  }, [selectedLegData, leg.pickupLocation, leg.pickupLocationType, leg.dropoffLocation, leg.dropoffLocationType, flightConnectionMode, legMoment]);

  const hasFlights = flightOptions.length > 0;

  return (
    <div className={s.legPanel}>
      {/* Leg Header */}
      <div className={s.legHeader}>
        <div className={s.legHeaderDot} style={{ background: color }} />
        <span className={s.legHeaderLabel}>{label}</span>
        <span className={s.legHeaderBadge} style={{ background: `${color}14`, color, border: `1.5px solid ${color}30` }}>
          {legMoment === "Arrival" ? "Airport → Hotel" : "Hotel → Airport"}
        </span>
      </div>

      {/* Flight Connection */}
      {hasFlights && (
        <div className={s.legSubSection}>
          <div className={s.sectionLabel}><span className={s.sectionDot} style={{ background: "#3b5bdb" }} />Flight Connection</div>
          <div className={s.connectionToggle}>
            <button type="button" className={`${s.connBtn} ${flightConnectionMode === "linked" ? s.connBtnLinked : ""}`} onClick={() => handleConnectionToggle("linked")}>
              <LinkIcon /> Link to Flight
            </button>
            <button type="button" className={`${s.connBtn} ${flightConnectionMode === "manual" ? s.connBtnManual : ""}`} onClick={() => handleConnectionToggle("manual")}>
              <UnlinkIcon /> Manual Entry
            </button>
          </div>
          {flightConnectionMode === "linked" && (
            <div className={s.flightLinkPanel}>
              <div className={s.mfRow2}>
                <div className={s.field}>
                  <label className={s.fieldLabel}>Select Flight Product <span className={s.req}>*</span></label>
                  <select className={s.input} value={leg._linkedFlightId || ""} onChange={(e) => handleFlightSelect(e.target.value)}>
                    <option value="">Choose a flight...</option>
                    {flightOptions.map((fo) => (<option key={fo.id} value={fo.id}>{fo.label}</option>))}
                  </select>
                </div>
                {leg._linkedFlightId && availableLegs.length > 0 && (
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Select Flight Leg</label>
                    <select className={s.input} value={leg._linkedLegId || ""} onChange={(e) => handleLegSelect(e.target.value)}>
                      <option value="">Choose a leg...</option>
                      {availableLegs.map((al, idx) => (
                        <option key={al.id} value={al.id}>
                          Leg {idx + 1}: {al.departure || "?"} → {al.arrival || "?"} ({al.direction}) {al.date ? `· ${al.date}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {selectedLegData && (
                <div className={s.linkedSummary}>
                  <div className={s.linkedSummaryIcon}><FlightIcon /></div>
                  <div className={s.linkedSummaryContent}>
                    <span className={s.linkedSummaryRoute}>{selectedLegData.departure || "?"} → {selectedLegData.arrival || "?"}</span>
                    <span className={s.linkedSummaryMeta}>
                      {selectedLegData.airlineCode && `${selectedLegData.airlineCode}${selectedLegData.flightNumber || ""}`}
                      {selectedLegData.date && ` · ${selectedLegData.date}`}
                      {selectedLegData.time && ` at ${selectedLegData.time}`}
                      {` · ${selectedLegData.direction}`}
                    </span>
                  </div>
                  <span className={s.linkedBadge}>Connected</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Route */}
      <div className={s.legSubSection}>
        <div className={s.sectionLabel}><span className={s.sectionDot} style={{ background: "#059669" }} />Route</div>
        <div className={s.routeTypeRow}>
          <LocationTypeSelector label="Pickup Location Type" required value={leg.pickupLocationType || ""} onChange={(v) => { setField("pickupLocationType", v); setField("pickupLocation", ""); setField("pickupLocationLabel", ""); }} />
          <div className={s.routeArrowWrap}><RouteArrow /></div>
          <LocationTypeSelector label="Drop-off Location Type" required value={leg.dropoffLocationType || ""} onChange={(v) => { setField("dropoffLocationType", v); setField("dropoffLocation", ""); setField("dropoffLocationLabel", ""); }} />
        </div>
        <div className={s.routeValueRow}>
          <div className={s.routeValueCard}>
            <div className={s.routeValueLabel}><span className={`${s.routeValueDot} ${s.routeValueDotPickup}`} />Pickup Location <span className={s.req}>*</span></div>
            {leg.pickupLocationType === "Airport" ? (
              <SearchSelect value={leg.pickupLocation || ""} options={airportOptions} onChange={(v) => { setField("pickupLocation", v); const o = airportOptions.find((a) => a.value === v); setField("pickupLocationLabel", o ? o.label : v); }} onSearch={handleAirportSearch} placeholder="Search airport..." initialOption={leg.pickupLocation ? { value: leg.pickupLocation, code: leg.pickupLocation, label: leg.pickupLocationLabel || leg.pickupLocation } : null} />
            ) : leg.pickupLocationType === "Hotel" ? (
              <SearchSelect value={leg.pickupLocation || ""} options={hotelOptions} onChange={(v) => { setField("pickupLocation", v); const o = hotelOptions.find((h) => h.value === v); setField("pickupLocationLabel", o ? o.label : v); }} onSearch={handleHotelSearch} placeholder="Search hotel..." initialOption={leg.pickupLocation ? { value: leg.pickupLocation, code: "", label: leg.pickupLocationLabel || leg.pickupLocation } : null} renderOption={(opt) => (<><span className={s.searchSelectName}>{opt.label}</span>{opt.code && <span className={s.searchSelectCode}>{opt.code}</span>}</>)} renderSelected={(opt) => (<><span className={s.searchSelectSelectedName}>{opt.label}</span>{opt.code && <span className={s.searchSelectSelectedCode}>{opt.code}</span>}</>)} />
            ) : (
              <input className={`${s.input} ${s.routeInput}`} placeholder="Enter pickup location..." value={leg.pickupLocation || ""} onChange={(e) => setField("pickupLocation", e.target.value)} />
            )}
            {leg.pickupLocationType && <span className={s.routeValueTag}>{LOCATION_TYPES.find((lt) => lt.key === leg.pickupLocationType)?.icon} {leg.pickupLocationType}</span>}
          </div>
          <div className={s.routeConnector}><div className={s.routeConnectorLine} /><div className={s.routeConnectorDot} /><div className={s.routeConnectorLine} /></div>
          <div className={s.routeValueCard}>
            <div className={s.routeValueLabel}><span className={`${s.routeValueDot} ${s.routeValueDotDropoff}`} />Drop-off Location <span className={s.req}>*</span></div>
            {leg.dropoffLocationType === "Airport" ? (
              <SearchSelect value={leg.dropoffLocation || ""} options={airportOptions} onChange={(v) => { setField("dropoffLocation", v); const o = airportOptions.find((a) => a.value === v); setField("dropoffLocationLabel", o ? o.label : v); }} onSearch={handleAirportSearch} placeholder="Search airport..." initialOption={leg.dropoffLocation ? { value: leg.dropoffLocation, code: leg.dropoffLocation, label: leg.dropoffLocationLabel || leg.dropoffLocation } : null} />
            ) : leg.dropoffLocationType === "Hotel" ? (
              <SearchSelect value={leg.dropoffLocation || ""} options={hotelOptions} onChange={(v) => { setField("dropoffLocation", v); const o = hotelOptions.find((h) => h.value === v); setField("dropoffLocationLabel", o ? o.label : v); }} onSearch={handleHotelSearch} placeholder="Search hotel..." initialOption={leg.dropoffLocation ? { value: leg.dropoffLocation, code: "", label: leg.dropoffLocationLabel || leg.dropoffLocation } : null} renderOption={(opt) => (<><span className={s.searchSelectName}>{opt.label}</span>{opt.code && <span className={s.searchSelectCode}>{opt.code}</span>}</>)} renderSelected={(opt) => (<><span className={s.searchSelectSelectedName}>{opt.label}</span>{opt.code && <span className={s.searchSelectSelectedCode}>{opt.code}</span>}</>)} />
            ) : (
              <input className={`${s.input} ${s.routeInput}`} placeholder="Enter drop-off location..." value={leg.dropoffLocation || ""} onChange={(e) => setField("dropoffLocation", e.target.value)} />
            )}
            {leg.dropoffLocationType && <span className={s.routeValueTag}>{LOCATION_TYPES.find((lt) => lt.key === leg.dropoffLocationType)?.icon} {leg.dropoffLocationType}</span>}
          </div>
        </div>
        {leg.pickupLocation && leg.dropoffLocation && leg.pickupLocation === leg.dropoffLocation && (
          <div className={s.warningStrip}><InfoIcon /> Pickup and drop-off locations are identical. Please verify.</div>
        )}
        {airportMismatch && <div className={s.warningStrip}><InfoIcon /> {airportMismatch}</div>}
      </div>

      {/* Schedule */}
      <div className={s.legSubSection}>
        <div className={s.sectionLabel}><span className={s.sectionDot} />Schedule</div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Pickup Date</label><input className={s.input} type="date" value={leg.pickupDate || ""} onChange={(e) => setField("pickupDate", e.target.value)} /></div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Pickup Time Type</label>
            <div className={s.pickupTimePills}>
              {PICKUP_TIME_TYPES.map((pt) => (
                <button key={pt.key} type="button" className={`${s.pickupTimePill} ${(leg.pickupTimeType || "exact") === pt.key ? s.pickupTimePillActive : ""}`} onClick={() => setField("pickupTimeType", pt.key)}>{pt.label}</button>
              ))}
            </div>
          </div>
        </div>
        {(leg.pickupTimeType || "exact") === "exact" && (
          <div className={s.mfRow2}>
            <div className={s.field}><label className={s.fieldLabel}>Pickup Time</label><input className={s.input} type="time" value={leg.pickupTime || ""} onChange={(e) => setField("pickupTime", e.target.value)} /></div>
            <div className={s.field} />
          </div>
        )}
        {(leg.pickupTimeType || "exact") === "tba" && (
          <div className={s.pickupTimeNotice}>Pickup Time: To Be Advised</div>
        )}
        {(leg.pickupTimeType || "exact") === "voucher" && (
          <div className={s.pickupTimeNotice}>Please follow the pickup instructions stated on this voucher.</div>
        )}
      </div>

      {/* Flight Details */}
      <div className={s.legSubSection}>
        <div className={s.sectionLabel}>
          <span className={s.sectionDot} style={{ background: "#3b5bdb" }} />
          {legMoment === "Arrival" ? "Arrival Flight Details" : "Departure Flight Details"}
          {flightConnectionMode === "linked" && selectedLegData && <span className={s.autoFilledBadge}>Auto-filled</span>}
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Airline Code</label><input className={s.input} placeholder="e.g. TK" value={leg.flightAirlineCode || ""} onChange={(e) => setField("flightAirlineCode", e.target.value)} style={{ textTransform: "uppercase" }} /></div>
          <div className={s.field}><label className={s.fieldLabel}>Flight Number</label><input className={s.input} placeholder="e.g. TK1234" value={leg.flightFlightNumber || ""} onChange={(e) => setField("flightFlightNumber", e.target.value)} style={{ textTransform: "uppercase" }} /></div>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Flight Date</label><input className={s.input} type="date" value={leg.flightDate || ""} onChange={(e) => setField("flightDate", e.target.value)} /></div>
          <div className={s.field}><label className={s.fieldLabel}>Flight Time</label><input className={s.input} type="time" value={leg.flightTime || ""} onChange={(e) => setField("flightTime", e.target.value)} /></div>
        </div>
      </div>

      {/* Per-leg instructions */}
      <div className={s.legSubSection}>
        <div className={s.sectionLabel}><span className={s.sectionDot} />{legMoment === "Arrival" ? "Arrival" : "Departure"} Instructions</div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Pickup Instructions</label>
          <textarea className={`${s.input} ${s.textarea}`} rows={2} placeholder="Where exactly to meet, sign board details, etc." value={leg.pickupInstructions || ""} onChange={(e) => setField("pickupInstructions", e.target.value)} />
        </div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Can't Find Transfer Instructions</label>
          <textarea className={`${s.input} ${s.textarea}`} rows={2} placeholder="What to do if the driver can't be found..." value={leg.cantFindInstructions || ""} onChange={(e) => setField("cantFindInstructions", e.target.value)} />
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Travel Disruptions</label>
            <textarea className={`${s.input} ${s.textarea}`} rows={2} placeholder="What happens if the flight is delayed or disrupted…" value={leg.travelDisruptionInfo || ""} onChange={(e) => setField("travelDisruptionInfo", e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Contact Info</label>
            <textarea className={`${s.input} ${s.textarea}`} rows={2} placeholder="Phone / email of the transfer operator for this leg…" value={leg.contactInfoSupplier || ""} onChange={(e) => setField("contactInfoSupplier", e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Main TransferForm
   ═══════════════════════════════════════════════════════════════════════════ */

const TransferForm = ({ form, f, flightProducts = [], hotelProducts = [], hasOtherProducts }) => {
  const [flightConnectionMode, setFlightConnectionMode] = useState(form._linkedFlightId ? "linked" : "manual");
  const [airportSearch, setAirportSearch] = useState("");
  const [hotelSearch, setHotelSearch] = useState("");

  // The whole of Geo Data, not the first 500 by name — the same cut the flight form had,
  // which stopped the list at "Bodø" and hid every terminal after it behind a search you
  // had to guess. A transfer leans on this harder than a flight does, because it is as
  // likely to start at a port or a station as at an airport.
  const { data: airportData } = useGetAirports({ limit: 20000, active: true, sortBy: 'name', sortOrder: 'ASC', search: airportSearch });
  const { data: hotelData } = useGetHotels({ limit: 500, search: hotelSearch });

  const airportOptions = useMemo(() =>
    (airportData || []).map((a) => ({
      value: a.code || a.iataCode || String(a.id),
      code: a.code || a.iataCode || "",
      label: a.name,
      _search: `${a.code || a.iataCode || ""} ${a.name} ${a.linkedCities || a.city || ""}`,
    })),
    [airportData]
  );

  const hotelOptions = useMemo(() =>
    (hotelData || []).map((h) => ({
      value: h.hotelName || String(h.id),
      code: h.city || "",
      label: h.hotelName,
      _search: `${h.hotelName} ${h.city || ""} ${h.country || ""}`,
    })),
    [hotelData]
  );

  const handleAirportSearch = useCallback((q) => setAirportSearch(q), []);
  const handleHotelSearch = useCallback((q) => setHotelSearch(q), []);

  const bookingHotelName = useMemo(() => {
    if (!hotelProducts || hotelProducts.length === 0) return "";
    const h = hotelProducts[0];
    return h.hotelName || h.name || h.form?.hotelName || "";
  }, [hotelProducts]);

  const flightOptions = useMemo(() => {
    return flightProducts.map((fp) => {
      const legs = fp.legs || [];
      const routeLabel = legs.length > 0
        ? legs.map((l) => `${l.departure || "?"} → ${l.arrival || "?"}`).join(", ")
        : "No legs";
      return {
        id: String(fp.id),
        label: `${fp.supplier || "Flight"} — ${routeLabel}`,
        supplier: fp.supplier,
        legs,
        flightType: fp.flightType,
        airlinePnr: fp.airlinePnr,
      };
    });
  }, [flightProducts]);

  const selectedFlight = flightOptions.find((fo) => fo.id === form._linkedFlightId);
  const availableLegs = selectedFlight?.legs || [];
  const selectedLeg = availableLegs.find((l) => l.id === form._linkedLegId);

  const moment = form.transferMoment || "";
  const isRoundTrip = moment === "RoundTrip";

  /* ── Round Trip leg setters ── */
  const setArrivalField = useCallback((key, val) => {
    f("arrivalLeg", (prev) => ({ ...(prev || {}), [key]: typeof val === "function" ? val((prev || {})[key]) : val }));
  }, [f]);

  const setDepartureField = useCallback((key, val) => {
    f("departureLeg", (prev) => ({ ...(prev || {}), [key]: typeof val === "function" ? val((prev || {})[key]) : val }));
  }, [f]);

  /* ── Handle moment change ── */
  const handleMomentChange = (newMoment) => {
    f("transferMoment", newMoment);

    if (newMoment === "RoundTrip") {
      f("transferContext", "within_travel");
      const arrInit = { pickupLocationType: "Airport", dropoffLocationType: "Hotel" };
      const depInit = { pickupLocationType: "Hotel", dropoffLocationType: "Airport" };
      if (bookingHotelName) {
        arrInit.dropoffLocation = bookingHotelName;
        arrInit.dropoffLocationLabel = bookingHotelName;
        depInit.pickupLocation = bookingHotelName;
        depInit.pickupLocationLabel = bookingHotelName;
      }
      f("arrivalLeg", (prev) => ({ ...(prev || {}), ...arrInit }));
      f("departureLeg", (prev) => ({ ...(prev || {}), ...depInit }));
    } else if (newMoment === "Arrival") {
      f("pickupLocationType", "Airport");
      f("dropoffLocationType", "Hotel");
      if (bookingHotelName && !form.dropoffLocation) {
        f("dropoffLocation", bookingHotelName);
        f("dropoffLocationLabel", bookingHotelName);
      }
    } else if (newMoment === "Departure") {
      f("pickupLocationType", "Hotel");
      f("dropoffLocationType", "Airport");
      if (bookingHotelName && !form.pickupLocation) {
        f("pickupLocation", bookingHotelName);
        f("pickupLocationLabel", bookingHotelName);
      }
    }
  };

  /* ── Single-leg: Handle flight link selection ── */
  const handleFlightSelect = (flightId) => {
    f("_linkedFlightId", flightId);
    f("_linkedLegId", "");
    f("transferContext", "within_travel");
    const flight = flightOptions.find((fo) => fo.id === flightId);
    if (flight && flight.legs.length > 0) {
      let autoLeg;
      if (moment === "Arrival") {
        const inbound = flight.legs.filter((l) => l.direction === "Inbound");
        autoLeg = inbound.length > 0 ? inbound[inbound.length - 1] : flight.legs[flight.legs.length - 1];
      } else {
        const outbound = flight.legs.filter((l) => l.direction === "Outbound");
        autoLeg = outbound.length > 0 ? outbound[0] : flight.legs[0];
      }
      if (autoLeg) {
        handleLegSelect(autoLeg.id, flight.legs, moment);
      }
    }
  };

  /* ── Single-leg: Handle leg selection ── */
  const handleLegSelect = (legId, legsOverride, momentOverride) => {
    f("_linkedLegId", legId);
    const legs = legsOverride || availableLegs;
    const leg = legs.find((l) => l.id === legId);
    if (!leg) return;

    const m = momentOverride || moment || "Arrival";

    f("flightAirlineCode", leg.airlineCode || "");
    f("flightFlightNumber", leg.flightNumber || "");
    f("flightDate", m === "Arrival" ? (leg.arrivalDate || leg.date || "") : (leg.date || ""));
    f("flightTime", m === "Arrival" ? (leg.arrivalTime || leg.time || "") : (leg.time || ""));

    if (m === "Arrival") {
      f("pickupLocation", leg.arrival || "");
      f("pickupLocationLabel", leg.arrivalName || leg.arrival || "");
      f("pickupLocationType", "Airport");
      if (bookingHotelName && !form.dropoffLocation) {
        f("dropoffLocation", bookingHotelName);
        f("dropoffLocationLabel", bookingHotelName);
        f("dropoffLocationType", "Hotel");
      }
      f("pickupDate", leg.arrivalDate || leg.date || "");
      f("pickupTime", leg.arrivalTime || "");
    } else if (m === "Departure") {
      f("dropoffLocation", leg.departure || "");
      f("dropoffLocationLabel", leg.departureName || leg.departure || "");
      f("dropoffLocationType", "Airport");
      if (bookingHotelName && !form.pickupLocation) {
        f("pickupLocation", bookingHotelName);
        f("pickupLocationLabel", bookingHotelName);
        f("pickupLocationType", "Hotel");
      }
      f("pickupDate", leg.date || "");
    }
  };

  /* ── Single-leg: Handle connection mode toggle ── */
  const handleConnectionToggle = (mode) => {
    setFlightConnectionMode(mode);
    if (mode === "manual") {
      f("_linkedFlightId", "");
      f("_linkedLegId", "");
    }
  };

  /* ── Lock context when other products exist ── */
  useEffect(() => {
    if (hasOtherProducts && (form.transferContext || "standalone") !== "within_travel") {
      f("transferContext", "within_travel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOtherProducts]);

  /* ── Single-leg: Auto-link to only flight ── */
  useEffect(() => {
    if (
      !isRoundTrip &&
      moment &&
      moment !== "Custom" &&
      flightConnectionMode === "linked" &&
      !form._linkedFlightId &&
      flightOptions.length === 1
    ) {
      handleFlightSelect(flightOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moment, flightOptions.length, flightConnectionMode, isRoundTrip]);

  /* ── Single-leg: Auto-fill hotel on edit ── */
  useEffect(() => {
    if (isRoundTrip || !bookingHotelName || !moment || moment === "Custom") return;
    if (moment === "Arrival" && form.dropoffLocationType === "Hotel" && !form.dropoffLocation) {
      f("dropoffLocation", bookingHotelName);
      f("dropoffLocationLabel", bookingHotelName);
    }
    if (moment === "Departure" && form.pickupLocationType === "Hotel" && !form.pickupLocation) {
      f("pickupLocation", bookingHotelName);
      f("pickupLocationLabel", bookingHotelName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingHotelName, moment, isRoundTrip]);

  /* ── Single-leg: Airport mismatch ── */
  const airportMismatch = useMemo(() => {
    if (isRoundTrip) return null;
    if (!selectedLeg || flightConnectionMode !== "linked") return null;
    if (moment === "Arrival" && form.pickupLocationType === "Airport") {
      const expected = selectedLeg.arrival || "";
      if (form.pickupLocation && form.pickupLocation !== expected) {
        return `Pickup airport (${form.pickupLocation}) doesn't match the flight's arrival airport (${expected || "unknown"}). Please check.`;
      }
    } else if (moment === "Departure" && form.dropoffLocationType === "Airport") {
      const expected = selectedLeg.departure || "";
      if (form.dropoffLocation && form.dropoffLocation !== expected) {
        return `Drop-off airport (${form.dropoffLocation}) doesn't match the flight's departure airport (${expected || "unknown"}). Please check.`;
      }
    }
    return null;
  }, [selectedLeg, form.transferMoment, form.pickupLocation, form.pickupLocationType, form.dropoffLocation, form.dropoffLocationType, flightConnectionMode, moment, isRoundTrip]);

  const hasFlights = flightOptions.length > 0;

  return (
    <>
      {/* ══════════════════════════════════════════════════
          SECTION 1 — TRANSFER CONTEXT
          ══════════════════════════════════════════════════ */}
      <div className={s.formCard}>
        <div className={s.sectionLabel}><span className={s.sectionDot} />Transfer Context</div>
        {hasOtherProducts || isRoundTrip ? (
          <div className={`${s.contextBtn} ${s.contextBtnActive}`} style={{ alignSelf: "flex-start", cursor: "default" }}>
            <CheckIcon /> Transfer within Travel Booking
          </div>
        ) : (
          <div className={s.contextToggle}>
            {[
              { key: "standalone", label: "Standalone Transfer" },
              { key: "within_travel", label: "Transfer within Travel Booking" },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                className={`${s.contextBtn} ${(form.transferContext || "standalone") === m.key ? s.contextBtnActive : ""}`}
                onClick={() => f("transferContext", m.key)}
              >
                {(form.transferContext || "standalone") === m.key && <CheckIcon />}
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════
          SECTION 2 — TRANSFER MOMENT
          ══════════════════════════════════════════════════ */}
      <div className={s.formCard}>
        <div className={s.sectionLabel}><span className={s.sectionDot} />Transfer Moment <span className={s.req}>*</span></div>
        <p className={s.sectionHint}>Choose the type of transfer. This determines how route and schedule fields are prefilled.</p>
        <div className={s.momentGrid}>
          {TRANSFER_MOMENTS.map((tm) => (
            <button
              key={tm.key}
              type="button"
              className={`${s.momentCard} ${moment === tm.key ? s.momentCardActive : ""} ${tm.key === "Arrival" ? s.momentArrival : tm.key === "Departure" ? s.momentDeparture : tm.key === "RoundTrip" ? s.momentRoundTrip : s.momentCustom}`}
              onClick={() => handleMomentChange(tm.key)}
            >
              <div className={`${s.momentIconWrap} ${moment === tm.key ? s.momentIconWrapActive : ""}`}>
                {tm.key === "Arrival" && <ArrivalIcon />}
                {tm.key === "Departure" && <DepartureIcon />}
                {tm.key === "RoundTrip" && <RoundTripIcon />}
                {tm.key === "Custom" && <CustomIcon />}
              </div>
              <div className={s.momentText}>
                <div className={s.momentLabel}>{tm.label}</div>
                <div className={s.momentDesc}>{tm.desc}</div>
              </div>
              {moment === tm.key && <div className={s.momentCheck}><CheckIcon /></div>}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          ROUND TRIP MODE
          ══════════════════════════════════════════════════ */}
      {isRoundTrip && (
        <>
          {/* Transfer Information (shared) */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Transfer Information</div>
            <div className={s.mfRow2}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Transfer Type <span className={s.req}>*</span></label>
                <div className={s.pillRow}>
                  {TRANSFER_TYPES.map((tt) => (
                    <button key={tt.key} type="button" className={`${s.pill} ${form.transferType === tt.key ? s.pillActive : ""}`} onClick={() => f("transferType", tt.key)}>
                      {form.transferType === tt.key && <CheckIcon />} {tt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Vehicle Type</label>
                <select className={s.input} value={form.vehicleType || ""} onChange={(e) => f("vehicleType", e.target.value)}>
                  <option value="">Select vehicle type...</option>
                  {EXTENDED_VEHICLE_TYPES.map((v) => (<option key={v.key} value={v.key}>{v.label}</option>))}
                </select>
              </div>
            </div>
          </div>

          {/* Arrival Leg */}
          <div className={s.formCard}>
            <RoundTripLegPanel
              leg={form.arrivalLeg || {}} setField={setArrivalField}
              legMoment="Arrival" label="Arrival Transfer" color="#059669"
              flightOptions={flightOptions} bookingHotelName={bookingHotelName}
              airportOptions={airportOptions} hotelOptions={hotelOptions}
              handleAirportSearch={handleAirportSearch} handleHotelSearch={handleHotelSearch}
            />
          </div>

          {/* Divider */}
          <div className={s.roundTripDivider}>
            <div className={s.roundTripDividerLine} />
            <span className={s.roundTripDividerLabel}>Return Journey</span>
            <div className={s.roundTripDividerLine} />
          </div>

          {/* Departure Leg */}
          <div className={s.formCard}>
            <RoundTripLegPanel
              leg={form.departureLeg || {}} setField={setDepartureField}
              legMoment="Departure" label="Departure Transfer" color="#3b5bdb"
              flightOptions={flightOptions} bookingHotelName={bookingHotelName}
              airportOptions={airportOptions} hotelOptions={hotelOptions}
              handleAirportSearch={handleAirportSearch} handleHotelSearch={handleHotelSearch}
              otherLeg={form.arrivalLeg}
            />
          </div>

          {/* Passenger Info (shared) */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Passenger Information</div>
            <div className={s.mfRow3}>
              <div className={s.field}><label className={s.fieldLabel}>Number of Adults</label><Stepper value={form.numberOfAdults || 1} min={1} onChange={(v) => f("numberOfAdults", v)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Number of Children</label><Stepper value={form.numberOfChildren || 0} min={0} onChange={(v) => f("numberOfChildren", v)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Lead Traveler Name</label><input className={s.input} placeholder="e.g. Sarah Johnson" value={form.leadTravelerName || ""} onChange={(e) => f("leadTravelerName", e.target.value)} /></div>
            </div>
          </div>

          {/* Supplier / Operational Info (shared) — Travel Disruptions & Contact Info
              now live per-leg in the Arrival / Departure Transfer sections above. */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Supplier / Operational Info</div>
            <div className={s.field}>
              <label className={s.fieldLabel}>Emergency Assistance</label>
              <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Emergency contacts and procedures..." value={form.emergencyAssistance || ""} onChange={(e) => f("emergencyAssistance", e.target.value)} />
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════
          SINGLE-LEG MODE (Arrival / Departure / Custom)
          ══════════════════════════════════════════════════ */}
      {!isRoundTrip && (
        <>
          {/* SECTION 3 — FLIGHT CONNECTION */}
          {hasFlights && moment && moment !== "Custom" && (
            <div className={s.formCard}>
              <div className={s.sectionLabel}><span className={s.sectionDot} style={{ background: "#3b5bdb" }} />Flight Connection</div>
              <p className={s.sectionHint}>Link this transfer to a flight to auto-populate route and flight details.</p>
              <div className={s.connectionToggle}>
                <button type="button" className={`${s.connBtn} ${flightConnectionMode === "linked" ? s.connBtnLinked : ""}`} onClick={() => handleConnectionToggle("linked")}>
                  <LinkIcon /> Link to Flight
                </button>
                <button type="button" className={`${s.connBtn} ${flightConnectionMode === "manual" ? s.connBtnManual : ""}`} onClick={() => handleConnectionToggle("manual")}>
                  <UnlinkIcon /> Manual Entry
                </button>
              </div>
              {flightConnectionMode === "linked" && (
                <div className={s.flightLinkPanel}>
                  <div className={s.mfRow2}>
                    <div className={s.field}>
                      <label className={s.fieldLabel}>Select Flight Product <span className={s.req}>*</span></label>
                      <select className={s.input} value={form._linkedFlightId || ""} onChange={(e) => handleFlightSelect(e.target.value)}>
                        <option value="">Choose a flight...</option>
                        {flightOptions.map((fo) => (<option key={fo.id} value={fo.id}>{fo.label}</option>))}
                      </select>
                    </div>
                    {form._linkedFlightId && availableLegs.length > 0 && (
                      <div className={s.field}>
                        <label className={s.fieldLabel}>Select Flight Leg</label>
                        <select className={s.input} value={form._linkedLegId || ""} onChange={(e) => handleLegSelect(e.target.value)}>
                          <option value="">Choose a leg...</option>
                          {availableLegs.map((leg, idx) => (
                            <option key={leg.id} value={leg.id}>
                              Leg {idx + 1}: {leg.departure || "?"} → {leg.arrival || "?"} ({leg.direction}) {leg.date ? `· ${leg.date}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {selectedLeg && (
                    <div className={s.linkedSummary}>
                      <div className={s.linkedSummaryIcon}><FlightIcon /></div>
                      <div className={s.linkedSummaryContent}>
                        <span className={s.linkedSummaryRoute}>{selectedLeg.departure || "?"} → {selectedLeg.arrival || "?"}</span>
                        <span className={s.linkedSummaryMeta}>
                          {selectedLeg.airlineCode && `${selectedLeg.airlineCode}${selectedLeg.flightNumber || ""}`}
                          {selectedLeg.date && ` · ${selectedLeg.date}`}
                          {selectedLeg.time && ` at ${selectedLeg.time}`}
                          {` · ${selectedLeg.direction}`}
                        </span>
                      </div>
                      <span className={s.linkedBadge}>Connected</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SECTION 4 — TRANSFER INFORMATION */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Transfer Information</div>
            <div className={s.mfRow2}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Transfer Type <span className={s.req}>*</span></label>
                <div className={s.pillRow}>
                  {TRANSFER_TYPES.map((tt) => (
                    <button key={tt.key} type="button" className={`${s.pill} ${form.transferType === tt.key ? s.pillActive : ""}`} onClick={() => f("transferType", tt.key)}>
                      {form.transferType === tt.key && <CheckIcon />} {tt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Vehicle Type</label>
                <select className={s.input} value={form.vehicleType || ""} onChange={(e) => f("vehicleType", e.target.value)}>
                  <option value="">Select vehicle type...</option>
                  {EXTENDED_VEHICLE_TYPES.map((v) => (<option key={v.key} value={v.key}>{v.label}</option>))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 5 — ROUTE */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} style={{ background: "#059669" }} />Route</div>
            <div className={s.routeTypeRow}>
              <LocationTypeSelector label="Pickup Location Type" required value={form.pickupLocationType || ""} onChange={(v) => { f("pickupLocationType", v); f("pickupLocation", ""); f("pickupLocationLabel", ""); }} />
              <div className={s.routeArrowWrap}><RouteArrow /></div>
              <LocationTypeSelector label="Drop-off Location Type" required value={form.dropoffLocationType || ""} onChange={(v) => { f("dropoffLocationType", v); f("dropoffLocation", ""); f("dropoffLocationLabel", ""); }} />
            </div>
            <div className={s.routeValueRow}>
              <div className={s.routeValueCard}>
                <div className={s.routeValueLabel}><span className={`${s.routeValueDot} ${s.routeValueDotPickup}`} />Pickup Location <span className={s.req}>*</span></div>
                {form.pickupLocationType === "Airport" ? (
                  <SearchSelect value={form.pickupLocation || ""} options={airportOptions} onChange={(v) => { f("pickupLocation", v); const o = airportOptions.find((a) => a.value === v); f("pickupLocationLabel", o ? o.label : v); }} onSearch={handleAirportSearch} placeholder="Search airport..." initialOption={form.pickupLocation ? { value: form.pickupLocation, code: form.pickupLocation, label: form.pickupLocationLabel || form.pickupLocation } : null} />
                ) : form.pickupLocationType === "Hotel" ? (
                  <SearchSelect value={form.pickupLocation || ""} options={hotelOptions} onChange={(v) => { f("pickupLocation", v); const o = hotelOptions.find((h) => h.value === v); f("pickupLocationLabel", o ? o.label : v); }} onSearch={handleHotelSearch} placeholder="Search hotel..." initialOption={form.pickupLocation ? { value: form.pickupLocation, code: "", label: form.pickupLocationLabel || form.pickupLocation } : null} renderOption={(opt) => (<><span className={s.searchSelectName}>{opt.label}</span>{opt.code && <span className={s.searchSelectCode}>{opt.code}</span>}</>)} renderSelected={(opt) => (<><span className={s.searchSelectSelectedName}>{opt.label}</span>{opt.code && <span className={s.searchSelectSelectedCode}>{opt.code}</span>}</>)} />
                ) : (
                  <input className={`${s.input} ${s.routeInput}`} placeholder="Enter pickup location..." value={form.pickupLocation || ""} onChange={(e) => f("pickupLocation", e.target.value)} />
                )}
                {form.pickupLocationType && <span className={s.routeValueTag}>{LOCATION_TYPES.find((lt) => lt.key === form.pickupLocationType)?.icon} {form.pickupLocationType}</span>}
              </div>
              <div className={s.routeConnector}><div className={s.routeConnectorLine} /><div className={s.routeConnectorDot} /><div className={s.routeConnectorLine} /></div>
              <div className={s.routeValueCard}>
                <div className={s.routeValueLabel}><span className={`${s.routeValueDot} ${s.routeValueDotDropoff}`} />Drop-off Location <span className={s.req}>*</span></div>
                {form.dropoffLocationType === "Airport" ? (
                  <SearchSelect value={form.dropoffLocation || ""} options={airportOptions} onChange={(v) => { f("dropoffLocation", v); const o = airportOptions.find((a) => a.value === v); f("dropoffLocationLabel", o ? o.label : v); }} onSearch={handleAirportSearch} placeholder="Search airport..." initialOption={form.dropoffLocation ? { value: form.dropoffLocation, code: form.dropoffLocation, label: form.dropoffLocationLabel || form.dropoffLocation } : null} />
                ) : form.dropoffLocationType === "Hotel" ? (
                  <SearchSelect value={form.dropoffLocation || ""} options={hotelOptions} onChange={(v) => { f("dropoffLocation", v); const o = hotelOptions.find((h) => h.value === v); f("dropoffLocationLabel", o ? o.label : v); }} onSearch={handleHotelSearch} placeholder="Search hotel..." initialOption={form.dropoffLocation ? { value: form.dropoffLocation, code: "", label: form.dropoffLocationLabel || form.dropoffLocation } : null} renderOption={(opt) => (<><span className={s.searchSelectName}>{opt.label}</span>{opt.code && <span className={s.searchSelectCode}>{opt.code}</span>}</>)} renderSelected={(opt) => (<><span className={s.searchSelectSelectedName}>{opt.label}</span>{opt.code && <span className={s.searchSelectSelectedCode}>{opt.code}</span>}</>)} />
                ) : (
                  <input className={`${s.input} ${s.routeInput}`} placeholder="Enter drop-off location..." value={form.dropoffLocation || ""} onChange={(e) => f("dropoffLocation", e.target.value)} />
                )}
                {form.dropoffLocationType && <span className={s.routeValueTag}>{LOCATION_TYPES.find((lt) => lt.key === form.dropoffLocationType)?.icon} {form.dropoffLocationType}</span>}
              </div>
            </div>
            {form.pickupLocation && form.dropoffLocation && form.pickupLocation === form.dropoffLocation && (
              <div className={s.warningStrip}><InfoIcon /> Pickup and drop-off locations are identical. Please verify.</div>
            )}
            {airportMismatch && <div className={s.warningStrip}><InfoIcon /> {airportMismatch}</div>}
          </div>

          {/* SECTION 6 — SCHEDULE */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Schedule</div>
            <div className={s.mfRow2}>
              <div className={s.field}><label className={s.fieldLabel}>Pickup Date</label><input className={s.input} type="date" value={form.pickupDate || ""} onChange={(e) => f("pickupDate", e.target.value)} /></div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Pickup Time Type</label>
                <div className={s.pickupTimePills}>
                  {PICKUP_TIME_TYPES.map((pt) => (
                    <button key={pt.key} type="button" className={`${s.pickupTimePill} ${(form.pickupTimeType || "exact") === pt.key ? s.pickupTimePillActive : ""}`} onClick={() => f("pickupTimeType", pt.key)}>{pt.label}</button>
                  ))}
                </div>
              </div>
            </div>
            {(form.pickupTimeType || "exact") === "exact" && (
              <div className={s.mfRow2}>
                <div className={s.field}><label className={s.fieldLabel}>Pickup Time</label><input className={s.input} type="time" value={form.pickupTime || ""} onChange={(e) => f("pickupTime", e.target.value)} /></div>
                <div className={s.field} />
              </div>
            )}
            {(form.pickupTimeType || "exact") === "tba" && (
              <div className={s.pickupTimeNotice}>Pickup Time: To Be Advised</div>
            )}
            {(form.pickupTimeType || "exact") === "voucher" && (
              <div className={s.pickupTimeNotice}>Please follow the pickup instructions stated on this voucher.</div>
            )}
          </div>

          {/* SECTION 7 — PASSENGER INFO */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Passenger Information</div>
            <div className={s.mfRow3}>
              <div className={s.field}><label className={s.fieldLabel}>Number of Adults</label><Stepper value={form.numberOfAdults || 1} min={1} onChange={(v) => f("numberOfAdults", v)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Number of Children</label><Stepper value={form.numberOfChildren || 0} min={0} onChange={(v) => f("numberOfChildren", v)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Lead Traveler Name</label><input className={s.input} placeholder="e.g. Sarah Johnson" value={form.leadTravelerName || ""} onChange={(e) => f("leadTravelerName", e.target.value)} /></div>
            </div>
          </div>

          {/* SECTION 8 — FLIGHT DETAILS */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}>
              <span className={s.sectionDot} style={{ background: "#3b5bdb" }} />
              {moment === "Arrival" ? "Arrival Flight Details" : moment === "Departure" ? "Departure Flight Details" : "Related Flight Details"}
              {flightConnectionMode === "linked" && selectedLeg && <span className={s.autoFilledBadge}>Auto-filled</span>}
            </div>
            <div className={s.mfRow2}>
              <div className={s.field}><label className={s.fieldLabel}>Airline Code</label><input className={s.input} placeholder="e.g. TK" value={form.flightAirlineCode || ""} onChange={(e) => f("flightAirlineCode", e.target.value)} style={{ textTransform: "uppercase" }} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Flight Number</label><input className={s.input} placeholder="e.g. TK1234" value={form.flightFlightNumber || ""} onChange={(e) => f("flightFlightNumber", e.target.value)} style={{ textTransform: "uppercase" }} /></div>
            </div>
            <div className={s.mfRow2}>
              <div className={s.field}><label className={s.fieldLabel}>Flight Date</label><input className={s.input} type="date" value={form.flightDate || ""} onChange={(e) => f("flightDate", e.target.value)} /></div>
              <div className={s.field}><label className={s.fieldLabel}>Flight Time</label><input className={s.input} type="time" value={form.flightTime || ""} onChange={(e) => f("flightTime", e.target.value)} /></div>
            </div>
          </div>

          {/* SECTION 9 — PER-LEG TRANSFER INSTRUCTIONS (moment-labeled).
              Pickup / Can't-find / Travel-disruption / Contact now live with the
              leg itself (Arrival or Departure), matching the round-trip panels and
              the per-leg layout of the transfer voucher. */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}>
              <span className={s.sectionDot} style={{ background: moment === "Arrival" ? "#059669" : moment === "Departure" ? "#3b5bdb" : "#f5a51e" }} />
              {moment === "Arrival" ? "Arrival Transfer Instructions" : moment === "Departure" ? "Departure Transfer Instructions" : "Transfer Instructions"}
            </div>
            <div className={s.field}>
              <label className={s.fieldLabel}>Pickup Instructions</label>
              <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Where exactly to meet, sign board details, etc." value={form.pickupInstructions || ""} onChange={(e) => f("pickupInstructions", e.target.value)} />
            </div>
            <div className={s.field}>
              <label className={s.fieldLabel}>Can't Find Transfer Instructions</label>
              <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="What to do if the driver can't be found..." value={form.cantFindInstructions || ""} onChange={(e) => f("cantFindInstructions", e.target.value)} />
            </div>
            <div className={s.mfRow2}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Travel Disruption Info</label>
                <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="What happens if flight is delayed..." value={form.travelDisruptionInfo || ""} onChange={(e) => f("travelDisruptionInfo", e.target.value)} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Contact Info — Transfer Supplier</label>
                <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Phone, email of transfer operator..." value={form.contactInfoSupplier || ""} onChange={(e) => f("contactInfoSupplier", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Booking-level operational info */}
          <div className={s.formCard}>
            <div className={s.sectionLabel}><span className={s.sectionDot} />Supplier / Operational Info</div>
            <div className={s.field}>
              <label className={s.fieldLabel}>Emergency Assistance</label>
              <textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Emergency contacts and procedures..." value={form.emergencyAssistance || ""} onChange={(e) => f("emergencyAssistance", e.target.value)} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

/* ─── Validation export ─────────────── */
export const hasInvalidTransfer = (form) => {
  if (!form.transferMoment) return true;
  if (!form.transferType) return true;

  if (form.transferMoment === "RoundTrip") {
    const a = form.arrivalLeg || {};
    const d = form.departureLeg || {};
    if (!a.pickupLocationType || !a.dropoffLocationType || !a.pickupLocation || !a.dropoffLocation) return true;
    if (!d.pickupLocationType || !d.dropoffLocationType || !d.pickupLocation || !d.dropoffLocation) return true;
    return false;
  }

  if (!form.pickupLocationType) return true;
  if (!form.dropoffLocationType) return true;
  if (!form.pickupLocation) return true;
  if (!form.dropoffLocation) return true;
  return false;
};

export default TransferForm;
